import type { Firestore } from 'firebase/firestore'
import type { Auth, GoogleAuthProvider, FacebookAuthProvider, User, NextOrObserver, Unsubscribe } from 'firebase/auth'
import {
  onAuthStateChanged as webOnAuthStateChanged,
  signInWithEmailAndPassword as webSignInWithEmail,
  createUserWithEmailAndPassword as webCreateUserWithEmail,
  sendEmailVerification as webSendEmailVerification,
  sendPasswordResetEmail as webSendPasswordReset,
  signOut as webSignOut,
  updateProfile as webUpdateProfile,
} from 'firebase/auth'
import type { Database } from 'firebase/database'
import type { FirebaseStorage } from 'firebase/storage'
import type { Functions } from 'firebase/functions'
import type { Messaging } from 'firebase/messaging'

// Mutable singletons — injected by each platform's firebase init file.
// Web: apps/safety-react/src/lib/firebase.ts calls initFirebaseCore at startup.
// Native: apps/safety-native/src/lib/firebase.ts will do the same.
export let db: Firestore = null!
export let auth: Auth = null!
export let rtdb: Database = null!
export let googleProvider: GoogleAuthProvider = null!
export let facebookProvider: FacebookAuthProvider = null!
export let getStorageInstance: () => Promise<FirebaseStorage> = null!
export let getFunctionsInstance: () => Promise<Functions> = null!
export let getMessagingInstance: () => Promise<Messaging | null> = () => Promise.resolve(null)

// Injectable auth operation functions — web SDK defaults; native overrides with RNFB modular API.
export interface AuthFns {
  onAuthStateChanged: (auth: Auth, observer: NextOrObserver<User>, error?: (error: Error) => void) => Unsubscribe
  signInWithEmailAndPassword: (auth: Auth, email: string, password: string) => Promise<{ user: User }>
  createUserWithEmailAndPassword: (auth: Auth, email: string, password: string) => Promise<{ user: User }>
  sendEmailVerification: (user: User) => Promise<void>
  sendPasswordResetEmail: (auth: Auth, email: string) => Promise<void>
  signOut: (auth: Auth) => Promise<void>
  updateProfile: (user: User, profile: { displayName?: string | null; photoURL?: string | null }) => Promise<void>
}

export let authFns: AuthFns = {
  onAuthStateChanged: webOnAuthStateChanged as AuthFns['onAuthStateChanged'],
  signInWithEmailAndPassword: webSignInWithEmail as AuthFns['signInWithEmailAndPassword'],
  createUserWithEmailAndPassword: webCreateUserWithEmail as AuthFns['createUserWithEmailAndPassword'],
  sendEmailVerification: webSendEmailVerification as AuthFns['sendEmailVerification'],
  sendPasswordResetEmail: webSendPasswordReset as AuthFns['sendPasswordResetEmail'],
  signOut: webSignOut as AuthFns['signOut'],
  updateProfile: webUpdateProfile as AuthFns['updateProfile'],
}

export function initFirebaseCore(instances: {
  db: Firestore
  auth: Auth
  rtdb: Database
  googleProvider: GoogleAuthProvider
  facebookProvider: FacebookAuthProvider
  getStorageInstance: () => Promise<FirebaseStorage>
  getFunctionsInstance: () => Promise<Functions>
  getMessagingInstance?: () => Promise<Messaging | null>
  authFns?: Partial<AuthFns>
}) {
  db = instances.db
  auth = instances.auth
  rtdb = instances.rtdb
  googleProvider = instances.googleProvider
  facebookProvider = instances.facebookProvider
  getStorageInstance = instances.getStorageInstance
  getFunctionsInstance = instances.getFunctionsInstance
  if (instances.getMessagingInstance) {
    getMessagingInstance = instances.getMessagingInstance
  }
  if (instances.authFns) {
    authFns = { ...authFns, ...instances.authFns }
  }
}
